/* eslint-disable clubmed/no-fake-component */
import React from 'react';
import { BucketFormikInput as FormikInput } from 'Src/types/forms';
import { Text } from 'Components/frameworks/text/Text';
import { useCountryList } from 'Src/hooks/use-country-list/useCountryList';
import type { DocumentFieldsVisibilitiesType } from './PersonInfoUtils';
import './PersonInfoUtils';
type Props = {
  attendeeIdentifier: string;
  dateFormat: string;
  dayPlaceholder: string;
  documentFieldsVisibilities: DocumentFieldsVisibilitiesType;
  monthPlaceholder: string;
  onKeyDown?: (e: KeyboardEvent) => void;
  yearPlaceholder: string;
  hideErrorMessage?: boolean;
};
export const LegalInfo = ({
  attendeeIdentifier,
  dateFormat,
  dayPlaceholder,
  documentFieldsVisibilities,
  monthPlaceholder,
  onKeyDown,
  yearPlaceholder,
  hideErrorMessage,
}: Props) => {
  const nationalitiesByLocale = useCountryList();
  const {
    idcardDocumentNumber,
    idcardExpirationDate,
    idcardIssuingDate,
    idcardAuthority,
    idcardBirthCountry,
    idcardBirthCity,
    idcardNationality,
    passportDocumentNumber,
    passportExpirationDate,
    passportIssuingDate,
    passportAuthority,
    passportBirthCountry,
    passportBirthCity,
    passportNationality,
    identityNumber,
  } = documentFieldsVisibilities;

  const renderDocumentNumber = (documentNumber) => {
    return (
      documentNumber?.isVisible && (
        <div className="p-10">
          <FormikInput
            dataTestId={`${documentNumber.type}_documentNumber_input`}
            hideErrorMessage={hideErrorMessage}
            isRequired={documentNumber.isRequired}
            label={documentNumber.label}
            name={`${attendeeIdentifier}.document.${documentNumber.type}.documentNumber`}
            onKeyDown={onKeyDown}
            type="text"
            validate={documentNumber.validator}
          />
        </div>
      )
    );
  };

  const renderExpirationDate = (expirationDate) => {
    return (
      expirationDate?.isVisible && (
        <div className="p-10">
          <FormikInput
            dataTestId={`${expirationDate.type}_expirationDate`}
            dateFormat={dateFormat}
            dayPlaceholder={dayPlaceholder}
            hideErrorMessage={hideErrorMessage}
            isRequired={expirationDate.isRequired}
            label={expirationDate.label}
            monthPlaceholder={monthPlaceholder}
            name={`${attendeeIdentifier}.document.${expirationDate.type}.expirationDate`}
            onKeyDown={onKeyDown}
            type="birthdate"
            validate={expirationDate.validator}
            yearPlaceholder={yearPlaceholder}
          />
        </div>
      )
    );
  };

  const renderIssuingDate = (issuingDate) => {
    return (
      issuingDate?.isVisible && (
        <div className="p-10">
          <FormikInput
            dataTestId={`${issuingDate.type}_issuingDate`}
            dateFormat={dateFormat}
            dayPlaceholder={dayPlaceholder}
            hideErrorMessage={hideErrorMessage}
            isRequired={issuingDate.isRequired}
            label={issuingDate.label}
            monthPlaceholder={monthPlaceholder}
            name={`${attendeeIdentifier}.document.${issuingDate.type}.issuingDate`}
            onKeyDown={onKeyDown}
            type="birthdate"
            validate={issuingDate.validator}
            yearPlaceholder={yearPlaceholder}
          />
        </div>
      )
    );
  };

  const renderBirthCountry = (birthCountry) => {
    return (
      birthCountry?.isVisible && (
        <div className="p-10">
          <FormikInput
            dataTestId={`${birthCountry.type}_birthCountry_input`}
            hideErrorMessage={hideErrorMessage}
            isRequired={birthCountry.isRequired}
            label={birthCountry.label}
            name={`${attendeeIdentifier}.document.${birthCountry.type}.birthCountry`}
            onClick={onKeyDown}
            type="select"
            validate={birthCountry.validator}
            options={nationalitiesByLocale}
          />
        </div>
      )
    );
  };

  const renderBirthCity = (birthCity) => {
    return (
      birthCity?.isVisible && (
        <div className="p-10">
          <FormikInput
            dataTestId={`${birthCity.type}_birthCity_input`}
            hideErrorMessage={hideErrorMessage}
            isRequired={birthCity.isRequired}
            label={birthCity.label}
            name={`${attendeeIdentifier}.document.${birthCity.type}.birthCity`}
            onKeyDown={onKeyDown}
            type="text"
            validate={birthCity.validator}
          />
        </div>
      )
    );
  };

  const renderAuthority = (authority) => {
    return (
      authority?.isVisible && (
        <div className="p-10">
          <FormikInput
            dataTestId={`${authority.type}_documentIssuingPlace_input`}
            hideErrorMessage={hideErrorMessage}
            isRequired={authority.isRequired}
            label={authority.label}
            name={`${attendeeIdentifier}.document.${authority.type}.issuingCountry`}
            onClick={onKeyDown}
            type="select"
            validate={authority.validator}
            options={nationalitiesByLocale}
          />
        </div>
      )
    );
  };

  const renderNationality = (nationality) => {
    return (
      nationality?.isVisible && (
        <div className="p-10">
          <FormikInput
            dataTestId={`${nationality.type}_nationalitiesDropDown`}
            hideErrorMessage={hideErrorMessage}
            isRequired={nationality.isRequired}
            label={nationality.label}
            name={`${attendeeIdentifier}.document.${nationality.type}.nationality`}
            onKeyDown={onKeyDown}
            options={nationalitiesByLocale}
            type="select"
            validate={nationality.validator}
          />
        </div>
      )
    );
  };

  return (
    <fieldset className="p-10">
      {renderBirthCity(idcardBirthCity)}
      {renderDocumentNumber(idcardDocumentNumber)}
      {renderExpirationDate(idcardExpirationDate)}
      {renderIssuingDate(idcardIssuingDate)}
      {renderBirthCountry(idcardBirthCountry)}
      {renderAuthority(idcardAuthority)}
      {renderNationality(idcardNationality)}

      {renderBirthCity(passportBirthCity)}
      {renderDocumentNumber(passportDocumentNumber)}
      {renderExpirationDate(passportExpirationDate)}
      {renderIssuingDate(passportIssuingDate)}
      {renderBirthCountry(passportBirthCountry)}
      {renderAuthority(passportAuthority)}
      {renderNationality(passportNationality)}

      {identityNumber?.isVisible && (
        <div className="p-10">
          <FormikInput
            dataTestId="identityNumber_input"
            hideErrorMessage={hideErrorMessage}
            isRequired={identityNumber.isRequired}
            label={identityNumber.label}
            name={`${attendeeIdentifier}.identityNumber`}
            onKeyDown={onKeyDown}
            type="text"
            validate={identityNumber.validator}
          />
          <Text className="mt-10" text={identityNumber.label} theme="text-sans-n2" />
        </div>
      )}
    </fieldset>
  );
};
